import { useCallback, useState } from 'react';
import { RespostasI } from '../interfaces/form-flow';
import {
  isAssinaturaElegibilidadeFailure,
  validarElegibilidadeAssinaturaGovbr,
} from '../services/assinaturaElegibilidade';
import { getDocumentosParaAssinatura } from '../utils/documentosAssinatura';

export type AssinarDialogState = {
  request: RespostasI;
  initialIndex: number;
};

export type AssinaturaGateState =
  | { type: 'nivel'; titulo: string }
  | { type: 'session'; message: string }
  | { type: 'error'; message: string };

export function useSolicitacaoAssinatura() {
  const [assinarDialog, setAssinarDialog] = useState<AssinarDialogState | null>(
    null,
  );
  const [assinaturaGate, setAssinaturaGate] = useState<AssinaturaGateState | null>(
    null,
  );
  const [validatingAssinatura, setValidatingAssinatura] = useState(false);
  const [signedDocsByRequest, setSignedDocsByRequest] = useState<
    Record<string, Set<string>>
  >({});

  const getSignedDocumentIds = useCallback(
    (identificador: string): Set<string> =>
      signedDocsByRequest[identificador] ?? new Set(),
    [signedDocsByRequest],
  );

  const closeAssinaturaGate = useCallback(() => {
    setAssinaturaGate(null);
  }, []);

  const reportNivelInsuficiente = useCallback((titulo: string) => {
    setAssinarDialog(null);
    setAssinaturaGate({ type: 'nivel', titulo });
  }, []);

  const requestAssinarDocumentos = useCallback(
    async (request: RespostasI, initialIndex = 0) => {
      const documentos = getDocumentosParaAssinatura(request);
      if (!documentos.length || validatingAssinatura) {
        return;
      }

      setValidatingAssinatura(true);
      setAssinaturaGate(null);

      try {
        const result = await validarElegibilidadeAssinaturaGovbr();

        if (isAssinaturaElegibilidadeFailure(result)) {
          if (result.reason === 'nivel') {
            setAssinaturaGate({ type: 'nivel', titulo: result.titulo });
          } else if (result.reason === 'session') {
            setAssinaturaGate({ type: 'session', message: result.message });
          } else {
            setAssinaturaGate({ type: 'error', message: result.message });
          }
          return;
        }

        setAssinarDialog({
          request,
          initialIndex: Math.min(initialIndex, documentos.length - 1),
        });
      } catch {
        setAssinaturaGate({
          type: 'error',
          message:
            'Não foi possível verificar sua elegibilidade para assinatura. Tente novamente.',
        });
      } finally {
        setValidatingAssinatura(false);
      }
    },
    [validatingAssinatura],
  );

  const closeAssinarDialog = useCallback(() => {
    setAssinarDialog(null);
  }, []);

  const markDocumentSigned = useCallback(
    (identificador: string, documentoId: string) => {
      setSignedDocsByRequest(prev => {
        const current = new Set(prev[identificador] ?? []);
        current.add(documentoId);
        return { ...prev, [identificador]: current };
      });
    },
    [],
  );

  return {
    assinarDialog,
    assinaturaGate,
    validatingAssinatura,
    getSignedDocumentIds,
    requestAssinarDocumentos,
    closeAssinaturaGate,
    reportNivelInsuficiente,
    closeAssinarDialog,
    markDocumentSigned,
  };
}
